import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Pleb, PlebData } from "../../models/Pleb";
import { BulkPayment } from "../../models/BulkPayment";
import { BulkQuoteResponse, ScanData, WalletServerInvoiceObject } from "../../repositories/WalletRepository";
import { AdditionalInfos, Transaction } from "../../models/Transactions";

export interface SendTransactionStateRecipient {
    lnurl?: string;
    invoice?: string;
    pleb?: Pleb | null;
    bulkPayment?: BulkPayment | null;
}

interface SendTransactionStateRecipientInternal {
    lnurl?: string;
    invoice?: string;
    pleb?: PlebData | null;
    bulkPayment?: BulkPayment | null;
}

export interface SendTransactionState {
    recipient: SendTransactionStateRecipient | null;
    scanData: ScanData | null;
    amount?: number;
    sendingFees?: number;
    comment?: string;
    anonymous?: boolean;
    membersOnly?: boolean;
    imageUrl?: string;
    additionalInfos?: AdditionalInfos;
    bulkQuoteResponse?: BulkQuoteResponse;
}

interface SendTransactionStateInternal {
    recipient: SendTransactionStateRecipientInternal | null;
    scanData: ScanData | null;
    amount?: number;
    sendingFees?: number;
    amountIncludesFees?: boolean;
    comment?: string;
    anonymous?: boolean;
    membersOnly?: boolean;
    imageUrl?: string;
    additionalInfos?: AdditionalInfos;
    bulkQuoteResponse?: BulkQuoteResponse;
    error: string;
    loading: boolean;
}

const initialState: SendTransactionStateInternal = {
    recipient: null,
    scanData: null,
    amount: undefined,
    sendingFees: undefined,
    amountIncludesFees: false,
    comment: undefined,
    imageUrl: undefined,
    additionalInfos: undefined,
    anonymous: false,
    membersOnly: false,
    bulkQuoteResponse: undefined,
    error: '',
    loading: false
};

const transactionSlice = createSlice({
    name: 'transaction',
    initialState,
    reducers: {
        setTransactionAmount: (state, action: PayloadAction<number | undefined>) => {
            state.amount = action.payload;
        },
        setTransactionAmountIncludesFees: (state, action: PayloadAction<boolean>) => {
            state.amountIncludesFees = action.payload;
        },
        setTransactionSendingFees: (state, action: PayloadAction<number | undefined>) => {
            state.sendingFees = action.payload;
        },
        setTransactionScanData: (state, action: PayloadAction<ScanData | null>) => {
            state.scanData = action.payload;
        },
        setTransactionBulkQuoteResponse: (state, action: PayloadAction<BulkQuoteResponse>) => {
            state.bulkQuoteResponse = action.payload;
        },
        setTransactionRecipient: (state, action: PayloadAction<SendTransactionStateRecipientInternal | null>) => {
            state.recipient = action.payload;
        },
        setTransactionComment: (state, action: PayloadAction<string>) => {
            state.comment = action.payload;
        },
        setTransactionImageUrl: (state, action: PayloadAction<string | undefined>) => {
            state.imageUrl = action.payload;
        },
        setTransactionAdditionalInfos: (state, action: PayloadAction<AdditionalInfos | undefined>) => {
            state.additionalInfos = action.payload;
        },
        setTransactionAnonymous: (state, action: PayloadAction<boolean>) => {
            state.anonymous = action.payload;
        },
        setTransactionMembersOnly: (state, action: PayloadAction<boolean>) => {
            state.membersOnly = action.payload;
        },
        resetTransaction: (state) => {
            return initialState;
        },
        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        }
    }
});

export const {
    setTransactionAmount,
    setTransactionSendingFees,
    setTransactionAmountIncludesFees,
    setTransactionBulkQuoteResponse,
    setTransactionRecipient,
    setTransactionScanData,
    setTransactionComment,
    setTransactionImageUrl,
    setTransactionAdditionalInfos,
    setTransactionAnonymous,
    setTransactionMembersOnly,
    resetTransaction,
    setError,
    setLoading
} = transactionSlice.actions;

export default transactionSlice.reducer; 